import React from 'react';
import { useTranslation } from 'react-i18next';
import { InputGroup } from '../../common/InputGroup';
import { DropdownMultiTypeahead } from '../../common/DropdownMultiTypeahead';
import { ProfileImageSection } from './ProfileImageSection';
import { PersonalInfoSection } from './PersonalInfoSection';
import { BaseFormLayout } from './BaseFormLayout';
import { City } from '../../../types/profile';
import { ServiceCategory } from '../../../types/service';
import FormLanguageSelector from '../../common/FormLanguageSelector';
import { useProfileStore } from '../../../stores/profileStore';
import { toast } from 'react-toastify';
import { useServiceCategorySelector } from '../../../hooks/useServiceCategorySelector';
import { createServiceGroupBy } from '../../../utils/serviceUtils';

interface FreelancerFormProps {
  firstName: string;
  lastName: string;
  phone: string;
  cityQuery: string;
  languages: string[];
  description: string | undefined;
  selectedServiceCategories: ServiceCategory[];
  errors: {
    firstName?: string;
    lastName?: string;
    city?: string;
    phone?: string;
    services?: string;
    languages?: string;
  };
  cityOptions: City[];
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onCitySelect: (city: City | null) => void;
  onLanguagesChange: (languages: string[]) => void;
  onDescriptionChange: (value: string) => void;
  onServiceCategoriesChange: (categories: ServiceCategory[]) => void;
  onBack?: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
}

export const FreelancerForm: React.FC<FreelancerFormProps> = ({
  firstName,
  lastName,
  phone,
  cityQuery,
  languages,
  description,
  selectedServiceCategories,
  errors,
  cityOptions,
  onFirstNameChange,
  onLastNameChange,
  onPhoneChange,
  onCitySelect,
  onLanguagesChange,
  onDescriptionChange,
  onServiceCategoriesChange,
  onBack,
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslation(['auth', 'settings', 'common']);

  const { suggestNewService } = useProfileStore();

  const {
    filteredOptions: filteredServiceOptions,
    searchInput: serviceSearchInput,
    setSearchInput: setServiceSearchInput,
  } = useServiceCategorySelector([]);

  const allServiceCategoryOptions = serviceSearchInput.trim() ? filteredServiceOptions : [];

  const handleServiceAdded = (service: ServiceCategory) => {
    onServiceCategoriesChange([...selectedServiceCategories, service]);
  };

  const handleCreateNewService = async (query: string) => {
    try {
      const newService = await suggestNewService({
        label: query,
        description: '',
      });

      handleServiceAdded(newService);

      toast.success(t('serviceAddedSuccess', { ns: 'settings', defaultValue: 'Service added!' }));
    } catch (err) {
      console.error('Error creating service:', err);
      toast.error(t('serviceAddError', { ns: 'settings', defaultValue: 'Could not add service.' }));
    }
  };

  return (
    <BaseFormLayout
      firstName={firstName}
      onBack={onBack}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    >
      <div className="font-ambit text-[1.5625rem] not-italic font-[900] leading-[1.875rem] text-secondary mb-4">
        {t('completeProfileStep.inscription', { ns: 'auth' })}
      </div>
      <div className="font-ambit text-[1.0625rem] not-italic font-normal leading-[1.5625rem] text-secondary mb-4">
        {t('completeProfileStep.professionalInfo', { ns: 'auth' })}
      </div>

      <ProfileImageSection />

      <PersonalInfoSection
        firstName={firstName}
        lastName={lastName}
        phone={phone}
        cityQuery={cityQuery}
        cityOptions={cityOptions}
        onFirstNameChange={onFirstNameChange}
        onLastNameChange={onLastNameChange}
        onPhoneChange={onPhoneChange}
        onCitySelect={onCitySelect}
        errors={errors}
      />

      <InputGroup
        label={t('servicesOffered')}
        required
        error={!!errors.services}
        errorMessage={errors.services}
      >
        <DropdownMultiTypeahead
          selected={selectedServiceCategories}
          onChange={onServiceCategoriesChange}
          options={allServiceCategoryOptions}
          onSearchChange={setServiceSearchInput}
          getOptionLabel={item => item.label}
          getOptionValue={item => item.id}
          placeholder={t('form_fields.servicesHint', { ns: 'common' })}
          className="mt-2"
          error={!!errors.services}
          onCreate={handleCreateNewService}
          groupBy={createServiceGroupBy(t)}
          inputMode="search"
        />
      </InputGroup>

      <FormLanguageSelector
        value={languages}
        onChange={onLanguagesChange}
        error={errors.languages}
        required
      />

      <p className="font-ambit text-[0.8125rem] not-italic font-normal leading-[1.375rem] text-[#3B5266] rounded-md border border-blue-100 bg-blue-50/70 px-3 py-2.5 mb-1">
        {t('professionalDescriptionNotice', { ns: 'auth' })}
      </p>
      <InputGroup
        label={t('professionalDescription', { ns: 'auth' })}
        value={description}
        onChange={e => onDescriptionChange(e.target.value)}
        as="textarea"
        placeholder={t('professionalDescriptionPlaceholder', { ns: 'auth' })}
        maxLength={1500}
      />
      <div className="text-right font-ambit text-[0.625rem] not-italic font-normal leading-[0.9375rem] text-secondary pb-4">
        {1500 - (description?.length || 0)} {t('charactersRemaining', { ns: 'auth' })}
      </div>
    </BaseFormLayout>
  );
};
